import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, Lock, Loader2, Check, Square, User } from 'lucide-react';

// Google Icon Component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const SignUpPage = () => {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    // Check for uppercase or special character
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
    if (!hasUppercase && !hasSpecialChar) {
      toast.error('Password must contain at least 1 uppercase letter or special character');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Service to continue');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await signUp(formData.email, formData.password, formData.companyName, formData.fullName);
      
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Account created! Please check your email to verify.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error(error.message || 'Failed to sign up with Google');
        setGoogleLoading(false);
      }
      // Don't set loading to false on success - redirect will happen
    } catch (err) {
      console.error('Google signup error:', err);
      toast.error('Failed to sign up with Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-[#EDF6F9] to-[#E2E8F0] flex items-center justify-center py-12 px-6" data-testid="signup-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-xl mx-auto mb-4 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#003B5C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create Your Account
          </h1>
          <p className="text-slate-600">Start tracking your emissions today</p>
        </div>

        {/* Google Sign Up Button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignUp}
          disabled={googleLoading || loading}
          className="w-full h-12 mb-6 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium"
          data-testid="google-signup-button"
        >
          {googleLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <GoogleIcon />
              Continue with Google
            </>
          )}
        </Button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-slate-500">or create account with email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName" className="text-[#003B5C] mb-2 block">
              Your Full Name
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="fullName"
                type="text"
                placeholder="John Smith"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="signup-name-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="companyName" className="text-[#003B5C] mb-2 block">
              Organisation Name
            </Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="companyName"
                type="text"
                placeholder="Your Organisation Pty Ltd"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="signup-company-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-[#003B5C] mb-2 block">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@company.com.au"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="signup-email-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-[#003B5C] mb-2 block">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="signup-password-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-[#003B5C] mb-2 block">
              Confirm Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="pl-10 h-12"
                required
                data-testid="signup-confirm-password-input"
              />
            </div>
          </div>

          {/* Terms of Service Checkbox */}
          <div className="flex items-start space-x-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
            <button
              type="button"
              onClick={() => setAcceptedTerms(!acceptedTerms)}
              className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                acceptedTerms 
                  ? 'bg-[#003B5C] border-[#003B5C]' 
                  : 'bg-white border-slate-300 hover:border-[#003B5C]'
              }`}
              data-testid="terms-checkbox"
            >
              {acceptedTerms && <Check className="w-3 h-3 text-white" />}
            </button>
            <div className="text-sm text-slate-600">
              <span>I agree to the </span>
              <Link to="/terms" target="_blank" className="text-[#003B5C] font-medium hover:underline">
                Terms of Service
              </Link>
              <span> and </span>
              <Link to="/privacy" target="_blank" className="text-[#003B5C] font-medium hover:underline">
                Privacy Policy
              </Link>
              <span>, and I understand that Lucent Sky ESG provides data calculation services only—not financial, legal, or advisory services.</span>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full bg-[#003B5C] text-white hover:bg-[#002A42] rounded-full h-12 text-lg font-medium disabled:opacity-50"
            data-testid="signup-submit-button"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#003B5C] font-medium hover:underline">
              Sign In
            </Link>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-200 text-center">
          <Link to="/" className="text-sm text-slate-600 hover:text-[#003B5C]">
            ← Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default SignUpPage;